import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { Archive, BellRing, CalendarClock, CheckCircle2, Coins, Copy, Flag, FlaskConical, Gavel, Hash, KeyRound, Landmark, Pencil, Plus, RotateCcw, RotateCw, Scale, Settings2, ShieldCheck, Sparkles, Trash2, Users, XCircle, type LucideIcon } from "lucide-react";
import { useApi, usePermission } from "@ncp/api-client";
import { useQuery } from "@tanstack/react-query";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, InlineAlert, Input, NativeSelect, PageHeader, Skeleton, Slider, Switch, Tabs, TabsList, TabsTrigger, Textarea, Tooltip, cn, copyText, notifySaved } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { useReleaseActions } from "../features/releases/api";
import { FLAG_CATEGORIES, numberRules, selectOptions, useDecisionControls, useFeatureFlags, useFlagActions, useSecretActions, useSecrets, useSettingActions, useSettingGroups, type FeatureFlag, type FlagInput, type SecretSummary, type SettingGroup, type SettingValue } from "../features/settings/api";

const GROUP_ICONS: Record<string, LucideIcon> = { Landmark, Hash, CalendarClock, Coins, Scale, BellRing, Sparkles, Archive, ShieldCheck, Gavel, Settings2 };
type Tab = "settings" | "flags" | "secrets";

/** Settings & Flags: schema-driven settings groups, feature flags with targeting, and the write-only secret store. */
export function SettingsPage() {
  const { t } = useTranslation();
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const { can } = usePermission();
  const firstAllowed: Tab = can("platform.settings.read") ? "settings" : can("platform.flags.read") ? "flags" : "secrets";
  const tab: Tab = tabParam === "flags" || tabParam === "secrets" ? tabParam : tabParam === undefined && !can("platform.settings.read") ? firstAllowed : "settings";
  const [creatingFlag, setCreatingFlag] = useState(false);
  const [creatingSecret, setCreatingSecret] = useState<string | null>(null);

  useHelpTopic({ title: t("admin:settings.title"), docsAnchor: "settings", body: (<><p>{t("admin:settings.help.what")}</p><p>{t("admin:settings.help.release")}</p><p>{t("admin:settings.help.secrets")}</p></>) });
  useBreadcrumbs(useMemo(() => tabParam ? [{ label: t(`admin:settings.tabs.${tab}`) }] : null, [tab, tabParam, t]));
  usePaletteActions(useMemo(() => [
    ...(can("platform.flags.write") ? [{ id: "flags.new", label: t("admin:settings.flags.newFlag"), icon: Flag, permission: "platform.flags.write", run: () => { navigate("/settings/flags"); setCreatingFlag(true); } }] : []),
    ...(can("platform.secrets.write") ? [{ id: "secrets.new", label: t("admin:settings.secrets.newSecret"), icon: KeyRound, permission: "platform.secrets.write", run: () => { navigate("/settings/secrets"); setCreatingSecret(""); } }] : []),
  ], [can, navigate, t]));

  const tabs: Array<{ key: Tab; label: string; icon: LucideIcon; permission: string }> = [
    { key: "settings", label: t("admin:settings.tabs.settings"), icon: Settings2, permission: "platform.settings.read" },
    { key: "flags", label: t("admin:settings.tabs.flags"), icon: Flag, permission: "platform.flags.read" },
    { key: "secrets", label: t("admin:settings.tabs.secrets"), icon: KeyRound, permission: "platform.secrets.read" },
  ];

  return (
    <div>
      <PageHeader title={t("admin:settings.title")} description={t("admin:settings.subtitle")} actions={
        tab === "flags" && can("platform.flags.write") ? <Button variant="gold" onClick={() => setCreatingFlag(true)}><Plus /> {t("admin:settings.flags.newFlag")}</Button>
          : tab === "secrets" && can("platform.secrets.write") ? <Button variant="gold" onClick={() => setCreatingSecret("")}><Plus /> {t("admin:settings.secrets.newSecret")}</Button>
          : null
      } />
      <Tabs value={tab} onValueChange={(v) => navigate(v === "settings" ? "/settings" : `/settings/${v}`)} className="mb-5">
        <TabsList variant="underline">
          {tabs.filter((x) => can(x.permission)).map((x) => <TabsTrigger key={x.key} value={x.key} variant="underline"><x.icon className="size-4" /> {x.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {tab === "settings" ? <SettingsGroups /> : null}
      {tab === "flags" ? <FlagsPanel creating={creatingFlag} onCreatingChange={setCreatingFlag} /> : null}
      {tab === "secrets" ? <SecretsPanel creatingKey={creatingSecret} onCreatingChange={setCreatingSecret} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------- settings groups

function SettingsGroups() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale.startsWith("ar");
  const [params, setParams] = useSearchParams();
  const groups = useSettingGroups();
  const [advanced, setAdvanced] = useState(false);
  const selectedKey = params.get("group") ?? groups.data?.[0]?.group.key ?? "general";
  const selected: SettingGroup | undefined = groups.data?.find((g) => g.group.key === selectedKey) ?? groups.data?.[0];
  const visible = selected?.settings.filter((s) => advanced || !s.advanced) ?? [];

  if (groups.isLoading) return <Skeleton className="h-96" />;
  return (
    <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
      <Card className="self-start">
        <CardHeader className="pb-2"><CardTitle className="text-sm">{t("admin:settings.groups")}</CardTitle></CardHeader>
        <CardContent className="p-2">
          <ul className="space-y-0.5">
            {(groups.data ?? []).map((g) => {
              const Icon = GROUP_ICONS[g.group.icon] ?? Settings2;
              const active = g.group.key === selected?.group.key;
              return (
                <li key={g.group.key}>
                  <button type="button" onClick={() => setParams((p) => { p.set("group", g.group.key); return p; }, { replace: true })} className={cn("flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start text-sm transition-colors", active ? "bg-aegold-50 text-aegold-900" : "hover:bg-sand-50")}>
                    <Icon className={cn("size-4 shrink-0", active ? "text-aegold-700" : "text-muted-foreground")} />
                    <span className="min-w-0 flex-1 truncate">{ar ? g.group.nameAr || g.group.name : g.group.name}</span>
                    {g.overridden > 0 ? <Badge variant="gold" size="sm">{g.overridden}</Badge> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <label className="mt-3 flex items-center gap-2 border-t border-sand-100 px-3 pt-3 text-xs text-muted-foreground"><Switch checked={advanced} onCheckedChange={setAdvanced} /> {t("admin:settings.showAdvanced")}</label>
        </CardContent>
      </Card>
      {selected ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">{(() => { const Icon = GROUP_ICONS[selected.group.icon] ?? Settings2; return <Icon className="size-4 text-aegold-700" />; })()} {ar ? selected.group.nameAr || selected.group.name : selected.group.name}</CardTitle>
            <CardDescription>{ar ? selected.group.descriptionAr || selected.group.description : selected.group.description}</CardDescription>
            {selected.group.key === "procedure" ? <TestEnvironmentLine /> : null}
          </CardHeader>
          <CardContent className="divide-y divide-sand-100">
            {visible.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">{t("admin:settings.noSettings")}</p> : visible.map((s) => <SettingRow key={s.key} setting={s} />)}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

/** The quiet line under Procedural fairness while the test-environment decision controls are in force, naming what they relax; nothing under Strict. */
function TestEnvironmentLine() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale.startsWith("ar");
  const controls = useDecisionControls();
  if (!controls.data?.relaxed) return null;
  return (
    <p role="note" className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
      <FlaskConical className="mt-0.5 size-3 shrink-0" aria-hidden />
      <span>
        <span className="font-medium">{t("admin:settings.testEnvironmentLine")}</span>{" "}
        {controls.data.relaxations.map((r, i) => <span key={r.code} title={ar ? r.ar : r.en}>{i > 0 ? (ar ? "؛ " : "; ") : ""}{t(`admin:settings.testEnvironmentCodes.${r.code}`, { defaultValue: ar ? r.ar : r.en })}</span>)}
      </span>
    </p>
  );
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  return JSON.stringify(v, null, 2);
}

function SettingRow({ setting: s }: { setting: SettingValue }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale.startsWith("ar");
  const navigate = useNavigate();
  const { can } = usePermission();
  const canWrite = can("platform.settings.write");
  const actions = useSettingActions();
  const releases = useReleaseActions();
  const [draft, setDraft] = useState<unknown>(s.value);
  const [jsonText, setJsonText] = useState(stringify(s.value));
  const [jsonError, setJsonError] = useState<string | null>(null);
  useEffect(() => { setDraft(s.value); setJsonText(stringify(s.value)); setJsonError(null); }, [s.value, s.updatedAt]);
  const dirty = s.valueType === "Json" ? jsonText !== stringify(s.value) : JSON.stringify(draft) !== JSON.stringify(s.value);

  const save = () => {
    let value: unknown = draft;
    if (s.valueType === "Json") {
      try { value = JSON.parse(jsonText); setJsonError(null); } catch { setJsonError(t("admin:settings.invalidJson")); return; }
    }
    // Failures of both steps are reported by useSave (the setting's and the release's), with their cause.
    actions.set.mutate({ key: s.key, value }, {
      onSuccess: (r) => {
        if (r.applied) { notifySaved(r, t("admin:settings.saved")); return; }
        releases.stage.mutate({ item: { definitionType: "setting", definitionKey: s.key, newValueJson: r.changeItemValueJson ?? JSON.stringify(value) }, titleHint: s.name }, {
          onSuccess: (cs) => { notifySaved(cs, { title: t("admin:settings.staged"), action: { label: t("admin:settings.openRelease"), onClick: () => navigate(`/releases/${cs.id}`) } }); },
        });
      },
    });
  };
  const reset = () => actions.reset.mutate(s.key, { onSuccess: (answer) => { notifySaved(answer, t("admin:settings.resetDone")); } });
  const busy = actions.set.isPending || releases.stage.isPending || actions.reset.isPending;
  const rules = numberRules(s);

  const control: ReactNode = (() => {
    switch (s.valueType) {
      case "Boolean":
        return <Switch checked={Boolean(draft)} disabled={!canWrite} onCheckedChange={(v) => setDraft(v)} />;
      case "Number":
        return (
          <div className="flex items-center gap-3">
            {rules.min !== undefined && rules.max !== undefined ? <Slider className="w-40" min={rules.min} max={rules.max} step={rules.step ?? 1} value={[Number(draft ?? rules.min)]} disabled={!canWrite} onValueChange={([v]) => setDraft(v)} /> : null}
            <Input type="number" className="w-28" dir="ltr" min={rules.min} max={rules.max} step={rules.step} value={draft === null || draft === undefined ? "" : String(draft)} disabled={!canWrite} onChange={(e) => setDraft(e.target.value === "" ? null : Number(e.target.value))} />
            {rules.unit ? <span className="text-xs text-muted-foreground">{rules.unit}</span> : null}
          </div>
        );
      case "Select":
        return (
          <NativeSelect value={String(draft ?? "")} disabled={!canWrite} onChange={(e) => setDraft(e.target.value)} className="w-72">
            {selectOptions(s).map((o) => <option key={o.value} value={o.value}>{ar && o.labelAr ? o.labelAr : o.label}</option>)}
          </NativeSelect>
        );
      case "Json":
        return (
          <div className="w-full">
            <Textarea rows={Math.min(12, Math.max(3, jsonText.split("\n").length))} value={jsonText} disabled={!canWrite} onChange={(e) => setJsonText(e.target.value)} dir="ltr" className="font-mono text-xs" aria-label={t("admin:settings.jsonEditor")} />
            {jsonError ? <p className="mt-1 text-xs text-danger-700">{jsonError}</p> : null}
          </div>
        );
      case "Secret":
        return <Input type="password" value={String(draft ?? "")} disabled={!canWrite} onChange={(e) => setDraft(e.target.value)} dir="ltr" className="w-72 font-mono" placeholder="••••••••" />;
      default:
        return <Input value={String(draft ?? "")} disabled={!canWrite} onChange={(e) => setDraft(e.target.value)} dir="auto" className="w-full max-w-md" />;
    }
  })();

  return (
    <div className="grid gap-3 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium">{ar && s.nameAr ? s.nameAr : s.name}</p>
          {s.requiresRelease ? <Tooltip content={t("admin:settings.releaseHint")}><Badge variant="camel" size="sm">{t("admin:settings.release")}</Badge></Tooltip> : null}
          {s.requiresRestart ? <Tooltip content={t("admin:settings.restartHint")}><Badge variant="outline" size="sm"><RotateCw /> {t("admin:settings.restart")}</Badge></Tooltip> : null}
          {s.isDefault ? <Badge variant="outline" size="sm">{t("admin:settings.default")}</Badge> : <Badge variant="gold" size="sm" dot>{t("admin:settings.changed")}</Badge>}
        </div>
        {(ar && s.descriptionAr) || s.description ? <p className="mt-1 text-sm text-muted-foreground">{ar && s.descriptionAr ? s.descriptionAr : s.description}</p> : null}
        <p className="mt-1 font-mono text-[11px] text-muted-foreground">{s.key}</p>
        {!s.isDefault && s.updatedAt ? <p className="text-[11px] text-muted-foreground">{t("admin:settings.updatedAt", { when: formatDateTime(s.updatedAt, locale) })}</p> : null}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">{control}</div>
        {!s.isDefault && s.valueType !== "Json" && s.valueType !== "Secret" ? <p className="text-[11px] text-muted-foreground">{t("admin:settings.defaultIs", { value: stringify(s.defaultValue) || "∅" })}</p> : null}
        {canWrite ? (
          <div className="flex items-center gap-2">
            <Button size="sm" variant={s.requiresRelease ? "outline" : "gold"} disabled={!dirty} loading={busy} onClick={save}>{s.requiresRelease ? t("admin:settings.release") : t("admin:common.save")}</Button>
            {!s.isDefault && !s.requiresRelease ? <Button size="sm" variant="ghost" loading={actions.reset.isPending} onClick={reset}><RotateCcw /> {t("admin:settings.resetToDefault")}</Button> : null}
            {dirty ? <Button size="sm" variant="ghost" onClick={() => { setDraft(s.value); setJsonText(stringify(s.value)); setJsonError(null); }}>{t("admin:common.cancel")}</Button> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- flags

function useRoleOptions() {
  const api = useApi();
  const { can } = usePermission();
  return useQuery({ queryKey: ["platform", "roles"], queryFn: () => api.get<Array<{ key: string; name: string; nameAr?: string | null }>>("/api/platform/roles"), enabled: can("platform.roles.read"), staleTime: 60_000 });
}

function FlagsPanel({ creating, onCreatingChange }: { creating: boolean; onCreatingChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const [params] = useSearchParams();
  const highlight = params.get("key");
  const flags = useFeatureFlags();
  const actions = useFlagActions();
  const [editing, setEditing] = useState<FeatureFlag | null>(null);
  const [deleting, setDeleting] = useState<FeatureFlag | null>(null);
  const canWrite = can("platform.flags.write");
  const byCategory = useMemo(() => {
    const map = new Map<string, FeatureFlag[]>();
    for (const f of flags.data ?? []) (map.get(f.category) ?? map.set(f.category, []).get(f.category)!).push(f);
    return [...map.entries()].sort((a, b) => FLAG_CATEGORIES.indexOf(a[0]) - FLAG_CATEGORIES.indexOf(b[0]));
  }, [flags.data]);
  useEffect(() => { if (highlight && flags.data) document.getElementById(`flag-${highlight}`)?.scrollIntoView({ block: "center" }); }, [highlight, flags.data]);

  if (flags.isLoading) return <Skeleton className="h-96" />;
  return (
    <div className="space-y-4">
      <InlineAlert tone="info">{t("admin:settings.flags.hint")}</InlineAlert>
      {byCategory.length === 0 ? <EmptyState icon={Flag} title={t("admin:settings.flags.empty")} /> : byCategory.map(([category, list]) => (
        <Card key={category}>
          <CardHeader className="pb-2"><CardTitle className="text-base">{t(`admin:settings.flags.categories.${category}`, { defaultValue: category })} <Badge variant="outline" size="sm" className="ms-2 tabular">{list.filter((f) => f.enabled).length}/{list.length}</Badge></CardTitle></CardHeader>
          <CardContent className="divide-y divide-sand-100 p-0">
            {list.map((f) => {
              const targeted = f.enabledForRoles.length + f.enabledForUsers.length > 0 || f.percentage < 100;
              const expired = f.expiresAt ? new Date(f.expiresAt) <= new Date() : false;
              return (
                <div key={f.key} id={`flag-${f.key}`} className={cn("flex flex-wrap items-center gap-3 px-5 py-3", highlight === f.key && "bg-aegold-50/60")}>
                  <Switch checked={f.enabled} disabled={!canWrite} onCheckedChange={(v) => actions.toggle.mutate({ key: f.key, enabled: v }, { onSuccess: (answer) => { notifySaved(answer, t(v ? "admin:settings.flags.toasts.enabled" : "admin:settings.flags.toasts.disabled")); } })} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{f.name}</p>
                      <span className="font-mono text-[11px] text-muted-foreground">{f.key}</span>
                      {f.known ? <Tooltip content={t("admin:settings.flags.knownHint")}><Badge variant="outline" size="sm">{t("admin:settings.flags.known")}</Badge></Tooltip> : <Badge variant="outline" size="sm">{t("admin:settings.flags.custom")}</Badge>}
                    </div>
                    {f.description ? <p className="text-sm text-muted-foreground">{f.description}</p> : null}
                    <div className="mt-1 flex flex-wrap gap-1">
                      <Badge variant={expired ? "danger" : f.enabled ? "success" : targeted ? "camel" : "outline"} size="sm" dot>{expired ? t("admin:settings.flags.expired") : f.enabled ? t("admin:settings.flags.on") : targeted ? t("admin:settings.flags.targeted") : t("admin:settings.flags.off")}</Badge>
                      {f.percentage < 100 ? <Badge variant="info" size="sm">{t("admin:settings.flags.percentage", { value: f.percentage })}</Badge> : null}
                      {f.enabledForRoles.length ? <Badge variant="gold" size="sm"><Users /> {t("admin:settings.flags.roles", { roles: f.enabledForRoles.join(", ") })}</Badge> : null}
                      {f.enabledForUsers.length ? <Badge variant="gold" size="sm">{t("admin:settings.flags.users", { count: f.enabledForUsers.length })}</Badge> : null}
                      {f.expiresAt && !expired ? <Badge variant="outline" size="sm">{t("admin:settings.flags.expires", { when: formatDateTime(f.expiresAt, locale) })}</Badge> : null}
                    </div>
                  </div>
                  {canWrite ? (
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(f)}><Pencil /> {t("admin:common.edit")}</Button>
                      {!f.known ? <Button size="sm" variant="ghost" onClick={() => setDeleting(f)}><Trash2 /></Button> : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      <FlagDialog open={creating || !!editing} flag={editing} onOpenChange={(o) => { if (!o) { onCreatingChange(false); setEditing(null); } }} />

      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <DialogContent size="sm">
          <DialogHeader><DialogTitle>{t("admin:common.delete")} · {deleting?.key}</DialogTitle><DialogDescription>{t("admin:settings.flags.deleteConfirm")}</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)}>{t("admin:common.cancel")}</Button>
            <Button variant="destructive" loading={actions.remove.isPending} onClick={() => deleting && actions.remove.mutate(deleting.key, { onSuccess: (answer) => { notifySaved(answer, t("admin:settings.flags.toasts.deleted")); setDeleting(null); } })}><Trash2 /> {t("admin:common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function toLocalInput(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function FlagDialog({ open, flag, onOpenChange }: { open: boolean; flag: FeatureFlag | null; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale.startsWith("ar");
  const actions = useFlagActions();
  const roles = useRoleOptions();
  const empty: FlagInput & { key: string; usersText: string; expires: string } = { key: "", name: "", description: "", category: "experiments", enabled: false, enabledForRoles: [], enabledForUsers: [], percentage: 100, usersText: "", expires: "" };
  const [form, setForm] = useState(empty);
  useEffect(() => {
    if (!open) return;
    setForm(flag ? { key: flag.key, name: flag.name, description: flag.description ?? "", category: flag.category, enabled: flag.enabled, enabledForRoles: flag.enabledForRoles, enabledForUsers: flag.enabledForUsers, percentage: flag.percentage, usersText: flag.enabledForUsers.join("\n"), expires: toLocalInput(flag.expiresAt) } : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flag]);
  const payload = (): FlagInput => ({ name: form.name.trim(), description: form.description || null, category: form.category, enabled: form.enabled, enabledForRoles: form.enabledForRoles, enabledForUsers: form.usersText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean), percentage: form.percentage, expiresAt: form.expires ? new Date(form.expires).toISOString() : null });
  const submit = () => {
    if (flag) actions.update.mutate({ key: flag.key, ...payload() }, { onSuccess: (answer) => { notifySaved(answer, t("admin:settings.flags.toasts.saved")); onOpenChange(false); } });
    else actions.create.mutate({ key: form.key.trim(), ...payload() }, { onSuccess: (answer) => { notifySaved(answer, t("admin:settings.flags.toasts.created")); onOpenChange(false); } });
  };
  const valid = form.name.trim().length > 0 && (flag || /^[a-zA-Z][a-zA-Z0-9_.-]{1,127}$/.test(form.key));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>{flag ? t("admin:settings.flags.editFlag") : t("admin:settings.flags.newFlag")}</DialogTitle>{flag ? <DialogDescription className="font-mono">{flag.key}</DialogDescription> : null}</DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {!flag ? <Field label={t("admin:settings.flags.fields.key")} hint={t("admin:settings.flags.fields.keyHint")} required>{(fid) => <Input id={fid} autoFocus value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} dir="ltr" className="font-mono" />}</Field> : null}
          <Field label={t("admin:common.name")} required className={flag ? "sm:col-span-2" : ""}>{(fid) => <Input id={fid} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} dir="auto" />}</Field>
          <Field label={t("admin:common.description")} className="sm:col-span-2">{(fid) => <Textarea id={fid} rows={2} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} dir="auto" />}</Field>
          <Field label={t("admin:settings.flags.fields.category")}>{(fid) => <NativeSelect id={fid} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{FLAG_CATEGORIES.map((c) => <option key={c} value={c}>{t(`admin:settings.flags.categories.${c}`)}</option>)}</NativeSelect>}</Field>
          <Field label={t("admin:settings.flags.fields.expiresAt")} hint={t("admin:settings.flags.fields.expiresHint")}>{(fid) => <Input id={fid} type="datetime-local" value={form.expires} onChange={(e) => setForm({ ...form, expires: e.target.value })} dir="ltr" />}</Field>
          <label className="flex items-start gap-3 rounded-lg border border-sand-200 p-3 text-sm sm:col-span-2"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} /><span><span className="font-medium">{t("admin:settings.flags.fields.enabled")}</span><span className="block text-xs text-muted-foreground">{t("admin:settings.flags.fields.enabledHint")}</span></span></label>
          <Field label={t("admin:settings.flags.fields.percentage")} hint={t("admin:settings.flags.fields.percentageHint")} className="sm:col-span-2">
            {() => (
              <div className="flex items-center gap-3">
                <Slider className="flex-1" min={0} max={100} step={5} value={[form.percentage]} onValueChange={([v]) => setForm({ ...form, percentage: v })} />
                <Input type="number" min={0} max={100} className="w-24" dir="ltr" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            )}
          </Field>
          <Field label={t("admin:settings.flags.fields.roles")} hint={t("admin:settings.flags.fields.rolesHint")}>
            {() => (
              <div className="max-h-44 overflow-y-auto rounded-lg border border-sand-200 p-2">
                {(roles.data ?? []).map((r) => (
                  <label key={r.key} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-sand-50"><Checkbox checked={form.enabledForRoles.includes(r.key)} onCheckedChange={(v) => setForm({ ...form, enabledForRoles: v === true ? [...form.enabledForRoles, r.key] : form.enabledForRoles.filter((k) => k !== r.key) })} /> {ar && r.nameAr ? r.nameAr : r.name} <span className="font-mono text-[11px] text-muted-foreground">{r.key}</span></label>
                ))}
                {roles.data?.length === 0 ? <p className="p-2 text-xs text-muted-foreground">{t("admin:common.none")}</p> : null}
              </div>
            )}
          </Field>
          <Field label={t("admin:settings.flags.fields.users")} hint={t("admin:settings.flags.fields.usersHint")}>{(fid) => <Textarea id={fid} rows={5} value={form.usersText} onChange={(e) => setForm({ ...form, usersText: e.target.value })} dir="ltr" className="font-mono text-xs" />}</Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" disabled={!valid} loading={actions.create.isPending || actions.update.isPending} onClick={submit}>{flag ? t("admin:common.save") : t("admin:common.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- secrets

function SecretsPanel({ creatingKey, onCreatingChange }: { creatingKey: string | null; onCreatingChange: (v: string | null) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = usePermission();
  const secrets = useSecrets();
  const actions = useSecretActions();
  const [rotating, setRotating] = useState<SecretSummary | null>(null);
  const [deleting, setDeleting] = useState<SecretSummary | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; message: string }>>({});
  const canWrite = can("platform.secrets.write");
  const copy = (text: string) => void copyText(text, t("admin:common.copied"));

  if (secrets.isLoading) return <Skeleton className="h-96" />;
  const list = secrets.data?.secrets ?? [];
  const dangling = secrets.data?.dangling ?? [];
  return (
    <div className="space-y-4">
      <InlineAlert tone="info">{t("admin:settings.secrets.hint")}</InlineAlert>
      {dangling.length > 0 ? (
        <InlineAlert tone="warning" title={t("admin:settings.secrets.dangling")}>
          <p className="text-xs">{t("admin:settings.secrets.danglingHint")}</p>
          <ul className="mt-2 space-y-1">
            {dangling.map((d) => <li key={d.key} className="flex flex-wrap items-center gap-2 text-xs"><code className="font-mono">secret://{d.key}</code><span className="text-muted-foreground">← {d.usedBy.join(", ")}</span>{canWrite ? <Button size="sm" variant="outline" onClick={() => onCreatingChange(d.key)}><Plus /> {t("admin:settings.secrets.createMissing")}</Button> : null}</li>)}
          </ul>
        </InlineAlert>
      ) : null}
      {list.length === 0 ? (
        <EmptyState icon={KeyRound} title={t("admin:settings.secrets.empty")} description={t("admin:settings.secrets.emptyHint")} action={canWrite ? <Button variant="gold" onClick={() => onCreatingChange("")}><Plus /> {t("admin:settings.secrets.newSecret")}</Button> : undefined} />
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-sand-600"><tr className="border-b border-sand-200 [&>th]:px-4 [&>th]:py-2 [&>th]:text-start"><th>{t("admin:common.key")}</th><th>{t("admin:settings.secrets.fields.value")}</th><th>{t("admin:settings.secrets.usedBy")}</th><th>{t("admin:common.updated")}</th><th /></tr></thead>
              <tbody>
                {list.map((s) => {
                  const test = testResult[s.key];
                  return (
                    <tr key={s.key} className="border-b border-sand-100 align-top last:border-0 [&>td]:px-4 [&>td]:py-3">
                      <td>
                        <p className="font-mono text-sm font-medium">{s.key}</p>
                        {s.description ? <p className="text-xs text-muted-foreground">{s.description}</p> : null}
                        <button type="button" onClick={() => copy(s.reference)} className="mt-1 inline-flex items-center gap-1 rounded bg-sand-100 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground hover:bg-sand-200" title={t("admin:settings.secrets.referenceHint")}><Copy className="size-3" /> {s.reference}</button>
                      </td>
                      <td>
                        <div className="flex flex-wrap items-center gap-1">
                          <code className="font-mono text-sm">{s.hint}</code>
                          {s.environmentVariable ? <Badge variant={s.environmentVariablePresent ? "success" : "warning"} size="sm">{t("admin:settings.secrets.fromEnv")} {s.environmentVariable} · {s.environmentVariablePresent ? t("admin:settings.secrets.envPresent") : t("admin:settings.secrets.envMissing")}</Badge> : null}
                          {s.expired ? <Badge variant="danger" size="sm" dot>{t("admin:settings.secrets.expired")}</Badge> : s.expiresAt ? <Badge variant="outline" size="sm">{t("admin:settings.secrets.expires", { when: formatDateTime(s.expiresAt, locale) })}</Badge> : null}
                        </div>
                        {test ? <p className={cn("mt-1 flex items-center gap-1 text-xs", test.ok ? "text-success-700" : "text-danger-700")}>{test.ok ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />} {test.message}</p> : null}
                      </td>
                      <td>{s.usedBy.length === 0 ? <span className="text-xs text-muted-foreground">{t("admin:settings.secrets.unused")}</span> : <div className="flex flex-wrap gap-1">{s.usedBy.map((u) => <Badge key={u} variant="outline" size="sm" className="font-mono">{u}</Badge>)}</div>}</td>
                      <td className="text-xs text-muted-foreground">{s.rotatedAt ? t("admin:settings.secrets.rotated", { when: formatDateTime(s.rotatedAt, locale), name: s.rotatedByName ?? "—" }) : t("admin:settings.secrets.neverRotated")}</td>
                      <td className="text-end">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" loading={actions.test.isPending && actions.test.variables === s.key} onClick={() => actions.test.mutate(s.key, { onSuccess: (r) => setTestResult((prev) => ({ ...prev, [s.key]: r })) })}><FlaskConical /> {t("admin:settings.secrets.test")}</Button>
                          {canWrite ? <Button size="sm" variant="outline" onClick={() => setRotating(s)}><RotateCw /> {t("admin:settings.secrets.rotate")}</Button> : null}
                          {canWrite ? <Button size="sm" variant="ghost" onClick={() => setDeleting(s)}><Trash2 /></Button> : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <SecretDialog open={creatingKey !== null || !!rotating} secret={rotating} presetKey={creatingKey ?? ""} onOpenChange={(o) => { if (!o) { onCreatingChange(null); setRotating(null); } }} />

      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <DialogContent size="sm">
          <DialogHeader><DialogTitle>{t("admin:common.delete")} · {deleting?.key}</DialogTitle><DialogDescription>{t("admin:settings.secrets.deleteConfirm")}</DialogDescription></DialogHeader>
          {deleting?.usedBy.length ? <InlineAlert tone="warning">{t("admin:settings.secrets.inUse", { by: deleting.usedBy.join(", ") })}</InlineAlert> : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)}>{t("admin:common.cancel")}</Button>
            <Button variant="destructive" loading={actions.remove.isPending} onClick={() => deleting && actions.remove.mutate({ key: deleting.key, force: deleting.usedBy.length > 0 }, { onSuccess: (answer) => { notifySaved(answer, t("admin:settings.secrets.toasts.deleted")); setDeleting(null); } })}><Trash2 /> {deleting?.usedBy.length ? t("admin:settings.secrets.forceDelete") : t("admin:common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SecretDialog({ open, secret, presetKey, onOpenChange }: { open: boolean; secret: SecretSummary | null; presetKey: string; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const actions = useSecretActions();
  const [form, setForm] = useState({ key: "", value: "", description: "", expires: "", env: "" });
  useEffect(() => { if (open) setForm({ key: secret?.key ?? presetKey, value: "", description: secret?.description ?? "", expires: toLocalInput(secret?.expiresAt), env: secret?.environmentVariable ?? "" }); }, [open, secret, presetKey]);
  const submit = () => actions.set.mutate({ key: form.key.trim(), value: form.value || null, description: form.description || null, expiresAt: form.expires ? new Date(form.expires).toISOString() : null, environmentVariable: form.env || null }, {
    onSuccess: (answer) => { notifySaved(answer, t(secret ? "admin:settings.secrets.toasts.rotated" : "admin:settings.secrets.toasts.saved")); onOpenChange(false); },
  });
  const valid = /^[a-zA-Z][a-zA-Z0-9_.-]{1,127}$/.test(form.key) && (secret ? true : form.value.length > 0 || form.env.length > 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader><DialogTitle>{secret ? t("admin:settings.secrets.rotateTitle") : t("admin:settings.secrets.newSecret")}</DialogTitle>{secret ? <DialogDescription className="font-mono">{secret.reference}</DialogDescription> : null}</DialogHeader>
        <div className="grid gap-3">
          {!secret ? <Field label={t("admin:settings.secrets.fields.key")} hint={t("admin:settings.secrets.fields.keyHint")} required>{(fid) => <Input id={fid} autoFocus value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} dir="ltr" className="font-mono" />}</Field> : null}
          <Field label={secret ? t("admin:settings.secrets.fields.newValue") : t("admin:settings.secrets.fields.value")} hint={t("admin:settings.secrets.fields.valueHint")} required={!secret}>{(fid) => <Input id={fid} type="password" autoComplete="new-password" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} dir="ltr" className="font-mono" />}</Field>
          <Field label={t("admin:settings.secrets.fields.description")}>{(fid) => <Input id={fid} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} dir="auto" />}</Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("admin:settings.secrets.fields.expiresAt")}>{(fid) => <Input id={fid} type="datetime-local" value={form.expires} onChange={(e) => setForm({ ...form, expires: e.target.value })} dir="ltr" />}</Field>
            <Field label={t("admin:settings.secrets.fields.env")} hint={t("admin:settings.secrets.fields.envHint")}>{(fid) => <Input id={fid} value={form.env} onChange={(e) => setForm({ ...form, env: e.target.value })} dir="ltr" className="font-mono" placeholder="NCP_SECRET_EIA_API_KEY" />}</Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" disabled={!valid} loading={actions.set.isPending} onClick={submit}><KeyRound /> {secret ? t("admin:settings.secrets.rotate") : t("admin:common.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
